import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ExternalLink, Plus, Trash2 } from "lucide-react";
import { rhm, apiError } from "../api";
import { Btn, Card, Field, Input, Modal, Select, Textarea } from "../ui";
import { ImageField } from "./websiteFields";

const TYPES = [
    { key: "product", label: "Supplements" },
    { key: "ebook", label: "Ebooks / guides" },
    { key: "consultation_pack", label: "Consultation packs" },
];

const CollectionRow = ({ c, index, total, editable, onPatch, onDelete, onMove, products }) => {
    const rule = c.rule || {};
    const types = rule.types || [];
    const slugs = rule.product_slugs || [];
    // Text fields save when you leave the field, not on every keystroke.
    const [text, setText] = useState({ name: c.name || "", tagline: c.tagline || "", description: c.description || "" });
    const commit = (key) => {
        if ((text[key] || "") !== (c[key] || "")) onPatch(c.id, { [key]: text[key] });
    };
    const toggleType = (key) => onPatch(c.id, {
        rule: { ...rule, types: types.includes(key) ? types.filter((t) => t !== key) : [...types, key] },
    });

    return (
        <div className="border border-white/10 p-4" data-testid={`collection-row-${c.slug}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-2">
                        {c.name}
                        <a href={`/shop/${c.slug}`} target="_blank" rel="noreferrer"
                            data-testid={`collection-open-${c.slug}`} className="text-gold">
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    </div>
                    <div className="mono text-[9px] tracking-[0.14em] text-zinc-500 mt-1">/shop/{c.slug}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <button disabled={!editable || index === 0} onClick={() => onMove(c, -1)}
                        data-testid={`collection-up-${c.slug}`} className="text-zinc-500 hover:text-white disabled:opacity-30">
                        <ArrowUp className="w-4 h-4" />
                    </button>
                    <button disabled={!editable || index === total - 1} onClick={() => onMove(c, 1)}
                        data-testid={`collection-down-${c.slug}`} className="text-zinc-500 hover:text-white disabled:opacity-30">
                        <ArrowDown className="w-4 h-4" />
                    </button>
                    {!c.is_system && (
                        <button disabled={!editable} onClick={() => onDelete(c)}
                            data-testid={`collection-delete-${c.slug}`} className="text-zinc-500 hover:text-red-400">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Field label="Name">
                    <Input value={text.name} disabled={!editable} data-testid={`collection-name-${c.slug}`}
                        onChange={(e) => setText({ ...text, name: e.target.value })} onBlur={() => commit("name")} />
                </Field>
                <Field label="Card icon">
                    <Select value={c.icon || "grid"} disabled={!editable} data-testid={`collection-icon-${c.slug}`}
                        options={["grid", "pill", "book", "stethoscope", "sparkles", "leaf", "gift", "flask"]}
                        onChange={(e) => onPatch(c.id, { icon: e.target.value })} />
                </Field>
                <Field label="Card tagline" className="md:col-span-2">
                    <Input value={text.tagline} disabled={!editable} data-testid={`collection-tagline-${c.slug}`}
                        onChange={(e) => setText({ ...text, tagline: e.target.value })} onBlur={() => commit("tagline")} />
                </Field>
                <Field label="Intro text on the collection page" className="md:col-span-2">
                    <Textarea rows={2} value={text.description} disabled={!editable}
                        data-testid={`collection-description-${c.slug}`}
                        onChange={(e) => setText({ ...text, description: e.target.value })}
                        onBlur={() => commit("description")} />
                </Field>
                <ImageField field={{ key: "image_url", label: "Card image (optional)" }} value={c.image_url}
                    onChange={(v) => onPatch(c.id, { image_url: v })} />
                <Field label="Show on the website" hint="Off hides the card and its page">
                    <Select value={c.visible === false ? "no" : "yes"} disabled={!editable}
                        data-testid={`collection-visible-${c.slug}`}
                        options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                        onChange={(e) => onPatch(c.id, { visible: e.target.value === "yes" })} />
                </Field>
            </div>

            <div className="mt-4 border-t border-white/8 pt-4">
                <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">WHAT GOES IN THIS COLLECTION</div>
                <div className="flex flex-wrap gap-2 mt-3">
                    {TYPES.map((t) => (
                        <button key={t.key} disabled={!editable} onClick={() => toggleType(t.key)}
                            data-testid={`collection-type-${c.slug}-${t.key}`}
                            className={`mono text-[10px] tracking-[0.14em] border px-3 py-2 transition-colors ${
                                types.includes(t.key) ? "border-gold text-gold" : "border-white/12 text-zinc-500"}`}>
                            {t.label.toUpperCase()}
                        </button>
                    ))}
                    <span className="text-xs text-zinc-500 self-center">
                        {types.length ? "" : "Nothing selected = every kind of item"}
                    </span>
                </div>
                <Field label="Or pick exact items" hint="Overrides the choices above" className="mt-4">
                    <div className="flex flex-wrap gap-2">
                        {products.map((p) => (
                            <button key={p.id} disabled={!editable}
                                data-testid={`collection-item-${c.slug}-${p.slug}`}
                                onClick={() => onPatch(c.id, {
                                    rule: {
                                        ...rule,
                                        product_slugs: slugs.includes(p.slug)
                                            ? slugs.filter((s) => s !== p.slug) : [...slugs, p.slug],
                                    },
                                })}
                                className={`text-[11px] border px-3 py-1.5 transition-colors ${
                                    slugs.includes(p.slug) ? "border-lime text-lime" : "border-white/12 text-zinc-500"}`}>
                                {p.name}
                            </button>
                        ))}
                    </div>
                </Field>
            </div>
        </div>
    );
};

export const CollectionsManager = ({ editable }) => {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", tagline: "", icon: "grid" });

    const { data, isLoading } = useQuery({
        queryKey: ["shop-collections-crm"],
        queryFn: async () => (await rhm.get("/site/collections")).data,
    });
    const { data: products = [] } = useQuery({
        queryKey: ["rhm-products"],
        queryFn: async () => (await rhm.get("/products")).data,
    });

    const refresh = () => qc.invalidateQueries({ queryKey: ["shop-collections-crm"] });
    const collections = data?.collections || [];
    const hub = data?.hub || {};

    const patch = useMutation({
        mutationFn: async ({ id, body }) => (await rhm.patch(`/site/collections/${id}`, body)).data,
        onSuccess: refresh,
        onError: (e) => toast.error(apiError(e)),
    });
    const create = useMutation({
        mutationFn: async (body) => (await rhm.post("/site/collections", body)).data,
        onSuccess: () => {
            toast.success("Collection created");
            setOpen(false);
            setForm({ name: "", tagline: "", icon: "grid" });
            refresh();
        },
        onError: (e) => toast.error(apiError(e)),
    });
    const remove = useMutation({
        mutationFn: async (id) => (await rhm.delete(`/site/collections/${id}`)).data,
        onSuccess: () => { toast.success("Collection deleted"); refresh(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const saveHub = useMutation({
        mutationFn: async (body) => (await rhm.put("/site/shop-hub", body)).data,
        onSuccess: () => { toast.success("Shop page saved"); refresh(); },
        onError: (e) => toast.error(apiError(e)),
    });

    const onPatch = (id, body) => patch.mutate({ id, body });
    const onMove = (c, delta) => {
        const ordered = [...collections];
        const i = ordered.findIndex((x) => x.id === c.id);
        const j = i + delta;
        if (j < 0 || j >= ordered.length) return;
        onPatch(c.id, { order: j });
        onPatch(ordered[j].id, { order: i });
    };

    const [hubForm, setHubForm] = useState(null);
    const h = hubForm || hub;

    if (isLoading) return <div className="mono text-xs text-zinc-500">LOADING COLLECTIONS…</div>;

    return (
        <div className="space-y-4" data-testid="collections-manager">
            <Card title="Shop page" action={editable && (
                <Btn data-testid="shop-hub-save" onClick={() => saveHub.mutate({
                    eyebrow: h.eyebrow || "", heading: h.heading || "", sub: h.sub || "",
                    featured_enabled: h.featured_enabled !== false,
                    featured_title: h.featured_title || "",
                    featured_slugs: h.featured_slugs || [],
                })}>SAVE</Btn>
            )}>
                <p className="text-sm text-zinc-500">
                    /shop shows these collections as cards. Visitors pick a card, then see only that group's items.
                </p>
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                    <Field label="Eyebrow">
                        <Input value={h.eyebrow || ""} disabled={!editable} data-testid="hub-eyebrow"
                            onChange={(e) => setHubForm({ ...h, eyebrow: e.target.value })} />
                    </Field>
                    <Field label="Heading">
                        <Input value={h.heading || ""} disabled={!editable} data-testid="hub-heading"
                            onChange={(e) => setHubForm({ ...h, heading: e.target.value })} />
                    </Field>
                    <Field label="Featured strip">
                        <Select value={h.featured_enabled === false ? "no" : "yes"} disabled={!editable}
                            data-testid="hub-featured-enabled"
                            options={[{ value: "yes", label: "Show" }, { value: "no", label: "Hide" }]}
                            onChange={(e) => setHubForm({ ...h, featured_enabled: e.target.value === "yes" })} />
                    </Field>
                    <Field label="Sub-line" className="md:col-span-2">
                        <Textarea rows={2} value={h.sub || ""} disabled={!editable} data-testid="hub-sub"
                            onChange={(e) => setHubForm({ ...h, sub: e.target.value })} />
                    </Field>
                    <Field label="Featured strip title">
                        <Input value={h.featured_title || ""} disabled={!editable} data-testid="hub-featured-title"
                            onChange={(e) => setHubForm({ ...h, featured_title: e.target.value })} />
                    </Field>
                    <Field label="Featured items" hint="Nothing picked = the newest purchasable items"
                        className="md:col-span-3">
                        <div className="flex flex-wrap gap-2">
                            {products.map((p) => {
                                const on = (h.featured_slugs || []).includes(p.slug);
                                return (
                                    <button key={p.id} disabled={!editable} data-testid={`hub-featured-${p.slug}`}
                                        onClick={() => setHubForm({
                                            ...h,
                                            featured_slugs: on
                                                ? (h.featured_slugs || []).filter((s) => s !== p.slug)
                                                : [...(h.featured_slugs || []), p.slug],
                                        })}
                                        className={`text-[11px] border px-3 py-1.5 transition-colors ${
                                            on ? "border-gold text-gold" : "border-white/12 text-zinc-500"}`}>
                                        {p.name}
                                    </button>
                                );
                            })}
                        </div>
                    </Field>
                </div>
            </Card>

            <Card title={`Collections (${collections.length})`} action={editable && (
                <Btn onClick={() => setOpen(true)} data-testid="collection-add">
                    <Plus className="w-3.5 h-3.5 mr-1 inline" /> COLLECTION
                </Btn>
            )}>
                <div className="space-y-4">
                    {collections.map((c, i) => (
                        <CollectionRow key={c.id} c={c} index={i} total={collections.length} editable={editable}
                            products={products} onPatch={onPatch} onMove={onMove}
                            onDelete={(col) => window.confirm(
                                `Delete "${col.name}"? Any menu link pointing to /shop/${col.slug} will stop working.`)
                                && remove.mutate(col.id)} />
                    ))}
                </div>
            </Card>

            <Modal open={open} onClose={() => setOpen(false)} title="New collection" testId="collection-modal">
                <div className="space-y-4">
                    <Field label="Name" required>
                        <Input value={form.name} data-testid="collection-new-name"
                            onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </Field>
                    <Field label="Card tagline">
                        <Input value={form.tagline} data-testid="collection-new-tagline"
                            onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
                    </Field>
                    <Field label="Icon">
                        <Select value={form.icon} data-testid="collection-new-icon"
                            options={["grid", "pill", "book", "stethoscope", "sparkles", "leaf", "gift", "flask"]}
                            onChange={(e) => setForm({ ...form, icon: e.target.value })} />
                    </Field>
                    <div className="flex gap-3">
                        <Btn disabled={!form.name || create.isPending} data-testid="collection-new-submit"
                            onClick={() => create.mutate({ ...form, order: collections.length })}>
                            {create.isPending ? "SAVING…" : "CREATE"}
                        </Btn>
                        <Btn variant="ghost" onClick={() => setOpen(false)}>CANCEL</Btn>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
